import React from 'react'
import {
  RealEstateCategory,
  RealEstateFeatures,
  SegmentationType,
  TransactionType,
} from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_APARTMENT_SELL_FLOW_PATH } from '../../../../../../services/routes/routes'
import { propertyOwnerApartmentSellSchema } from './propertyOwnerApartmentSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import {
  bathroomField,
  estateFeatures,
  floorAndTotalFloor,
  interiorQuality,
  moveInDate,
  noBedrooms,
  parkingSpace,
  rentalIncome,
  rentSubsidy,
  usableAreaField,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  priceField,
  priceMaximizer,
  purchasePriceOnRequestToggle,
  roomField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { priceToApiSell } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { RealtorLeadEngine } from '../../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'
import { apartmentSellBuildingTypeOptions } from '../common'

const propertyOwnerApartmentSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isInternationalAddressOption={true} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentOrHouseSell'
        properties={[
          livingSpaceField('leftOnAll'),
          roomField(),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('leftOnAll')),
          purchasePriceOnRequestToggle(),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        properties={[
          apartmentSellEstateFeatures(),
          interiorQuality(),
          buildingTypeField('apartment', apartmentSellBuildingTypeOptions, getDefaultClasses('full')),
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          moveInDate(getDefaultClasses('left')),
          rentSubsidy(getDefaultClasses('right')),
          floorAndTotalFloor('left'),
          usableAreaField(undefined, getDefaultClasses('rightOnDesk')),
          rentalIncome(),
          parkingSpace({
            showParkingSpaceType: true,
            hasGarageLabel: true,
            parkingSpaceRentTypeLabel: 'RentBuyingPrice',
          }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const apartmentSellEstateFeaturesList = [
  RealEstateFeatures.EQUIPPED_KITCHEN,
  RealEstateFeatures.ELEVATOR,
  RealEstateFeatures.BALCONY_TERRACE,
  RealEstateFeatures.GUEST_TOILET,
  RealEstateFeatures.SHARED_GARDEN,
  RealEstateFeatures.BASEMENT,
  RealEstateFeatures.STEPLESS_ACCESSIBLE,
  RealEstateFeatures.SUMMER_RESIDENCE_PRACTICAL,
  RealEstateFeatures.MONUMENT,
]

export const apartmentSellEstateFeatures = () => estateFeatures(apartmentSellEstateFeaturesList)

export const propertyOwnerApartmentSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_APARTMENT_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_APARTMENT_SELL,
    validationSchema: propertyOwnerApartmentSellSchema,
    priceToApiMapper: priceToApiSell,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    rented: false,
  },
  flowScreens: propertyOwnerApartmentSellFlowScreens,
}
